import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  isAuthExpired,
  saveAuthState,
  validPassword,
} from '~/utilities/authentication';

export interface AuthState {
  error?: string | null;
  authenticated: boolean;
  authenticating: 'idle' | 'pending' | 'succeeded' | 'failed';
}

export const initialState: AuthState = {
  authenticated: !isAuthExpired(),
  authenticating: 'idle',
};

export const login = createAsyncThunk(
  'auth/login',
  async (password: string) => {
    const valid = await validPassword(password);
    if (!valid) {
      throw new Error(
        'Sorry, that password is as mismatched as socks in a dryer. Try again!'
      );
    }
    return { authenticated: true };
  }
);

export const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.authenticated = false;
      saveAuthState(false);
    },
  },
  extraReducers: (builder) => {
    builder.addCase(login.pending, (state) => {
      state.authenticating = 'pending';
      state.error = undefined;
      state.authenticated = false;
      saveAuthState(false);
    });
    builder.addCase(login.fulfilled, (state, action) => {
      state.authenticating = 'succeeded';
      state.authenticated = action.payload.authenticated;
      saveAuthState(action.payload.authenticated);
    });
    builder.addCase(login.rejected, (state, action) => {
      state.authenticating = 'failed';
      state.error = action.error.message ?? null;
      saveAuthState(false);
    });
  },
});

export const { logout } = authSlice.actions;

type SliceState = { auth: AuthState };
export const selectIsAuthenticated = (state: SliceState) =>
  state.auth.authenticated === true;

export const selectIsAuthenticating = (state: SliceState) =>
  state.auth.authenticating === 'pending';

export const selectAuthError = (state: SliceState) => state.auth.error;

export default authSlice.reducer;
